import React from 'react';
import { withTranslation } from 'react-i18next';
import { withTheme } from 'styled-components';
import styled from 'styled-components';
import ScannedAttendeeItem from './ScannedAttendeeItem';
import Button from '../../components/shared/Button';
import Icon from '../../components/icons/Icon';
import IconScannedPeople from '../../components/icons/IconScannedPeople';
import InfiniteScroll from '../../components/shared/InfiniteScroll';
import QRCodeScanner from '../../components/qr-scanner';
import AlertModal from '../../components/shared/AlertModal';
import { extractAttendeeIdFromQR, extractEventIdFromQR } from '../../helpers/qrCode';

const Container = styled.div`
  padding: 20px 0 ${props => props.$isSearchActive ? '20px' : '120px'} 0;
  min-height: calc(100vh - 100px);
`;

const HeaderSection = styled.div`
  background: white;
  border-radius: 8px;
  margin: 0 10px 20px 10px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ScanIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: ${props => props.theme.mainColor};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px auto;
`;

const Title = styled.h2`
  font-size: 1.8rem;
  color: #333;
  margin: 0 0 10px 0;
  font-weight: 600;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
  font-size: 1.4rem;
`;

const ActionsContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 20px 20px 35px 20px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

const ScanButton = styled(Button)`
  width: 100%;
  padding: 15px;
  font-size: 1.6rem;
  font-weight: 600;
  border-radius: 8px;
  margin-bottom: 15px;
`;

const ExportLink = styled.div`
  text-align: center;
`;

const ExportButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.mainColor};
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
`;

class ScannedAttendees extends React.Component {
  state = {
    showScanner: false,
    showEmailSuccessModal: false,
    showErrorModal: false,
    errorType: null
  };

  componentDidMount() {
    this._isMounted = true;
  }

  componentDidUpdate(prevProps) {
    if (this.props.exportEmailSuccess && !prevProps.exportEmailSuccess) {
      this.setState({ showEmailSuccessModal: true });
      this.props.resetExportEmailSuccess();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  handleCloseEmailSuccessModal = () => {
    this.setState({ showEmailSuccessModal: false });
  };

  onClickScanAttendee = () => {
    this.setState({ showScanner: true });
  };

  onClickExportEmail = () => {
    this.props.exportScannedAttendeesEmail({
      ...this.props.getSharedApiParams()
    });
  };

  handleScanResult = (result) => {
    // QR code is already validated by QRCodeScanner
    const attendeeId = extractAttendeeIdFromQR(result);
    const scannedEventId = extractEventIdFromQR(result);
    const currentEventId = parseInt(this.props.matchParams.eventId);

    // Check if QR code is from a different event
    if (scannedEventId && scannedEventId !== currentEventId) {
      if (this._isMounted) {
        this.setState({
          showScanner: false,
          showErrorModal: true,
          errorType: 'wrong_event'
        });
      }
      return;
    }

    if (attendeeId) {
      this.createScanNote(attendeeId);
      this.addToFavorites(attendeeId);
      this.redirectToAttendeeDetails(attendeeId);
    }

    if (this._isMounted) {
      this.setState({ showScanner: false });
    }
  };

  createScanNote = (attendeeId) => {
    const { postAttendeeNotes } = this.props;

    postAttendeeNotes({
      scanned_attendee_id: attendeeId,
      notes: null
    });
  };

  addToFavorites = (attendeeId) => {
    const { appId, eventId } = this.props.matchParams;
    const { postFavorite, updateFavModel } = this.props;

    const favoriteData = {
      app_id: +appId,
      event_id: +eventId,
      favorite_id: attendeeId,
      favorite_type: 'attendee'
    };
    postFavorite(favoriteData);

    updateFavModel({
      data: { id: attendeeId, is_favorite: true },
      type: 'attendee'
    });
  };

  redirectToAttendeeDetails = (attendeeId) => {
    const { appId, eventId } = this.props.matchParams;

    const attendeeDetailsUrl = `/apps/${appId}/events/${eventId}/scanned-attendees/attendee/${attendeeId}`;

    this.props.history.push(attendeeDetailsUrl);
  };

  handleCloseScanner = () => {
    this.setState({ showScanner: false });
  };

  handleCloseErrorModal = () => {
    this.setState({ showErrorModal: false, errorType: null });
  };

  handleTryAgainAfterError = () => {
    this.setState({ showErrorModal: false, errorType: null, showScanner: true });
  };

  render() {
    const {
      t,
      theme,
      scannedAttendees,
      scannedAttendeesIds,
      user,
      matchParams,
      hasMoreScannedAttendees,
      isLoadingScannedAttendees,
      fetchScannedAttendees,
      event,
      presenceIds,
      whosHere
    } = this.props;

    const { showScanner, showEmailSuccessModal, showErrorModal } = this.state;
    const searchValue = this.props.global.searchValue || '';
    const isSearchActive = this.props.header?.showSearchBar || false;

    if (showScanner) {
      return (
        <QRCodeScanner
          onScan={this.handleScanResult}
          onClose={this.handleCloseScanner}
        />
      );
    }

    return (
      <Container $isSearchActive={isSearchActive}>
        {!isSearchActive && (
          <HeaderSection>
            <ScanIcon>
              <Icon icon={IconScannedPeople} color="white" width="40px" height="40px" />
            </ScanIcon>
            <Title>{t('people_you_scanned')}</Title>
          </HeaderSection>
        )}

        {scannedAttendeesIds.length === 0 ? (
          <EmptyState>
            {searchValue ? t('no_attendee_found') : t('no_contacts_scanned_yet')}
          </EmptyState>
        ) : (
          <InfiniteScroll
            loadMore={fetchScannedAttendees}
            hasMore={hasMoreScannedAttendees}
            isLoading={isLoadingScannedAttendees}
          >
            {scannedAttendeesIds.map(key => {
              const attendeeId = key.id || key;
              const attendee = scannedAttendees[attendeeId];
              if (!attendee) {
                console.warn(`Attendee with key ${attendeeId} not found in scannedAttendees`);
                return null;
              }
              return (
                <ScannedAttendeeItem
                  key={attendee.id}
                  id={attendee.id}
                  isOnline={presenceIds.indexOf(attendee.id) !== -1}
                  history={this.props.history}
                  {...{ t, attendee, theme, matchParams, user, whosHere }}
                />
              );
            })}
          </InfiniteScroll>
        )}

        {!isSearchActive && (
          <ActionsContainer>
            <ScanButton btntype="primary" onClick={this.onClickScanAttendee}>
              {t('scan_an_attendee')}
            </ScanButton>

            {event?.scan_notes_allow_export && scannedAttendeesIds.length > 0 && (
              <ExportLink>
                <ExportButton onClick={this.onClickExportEmail}>
                  {t('export_by_email')}
                </ExportButton>
              </ExportLink>
            )}
          </ActionsContainer>
        )}

        <AlertModal
          open={showEmailSuccessModal}
          onClose={this.handleCloseEmailSuccessModal}
          theme={theme}
          message={t('export_email_success')}
          buttonText={t('close')}
        />

        <AlertModal
          open={showErrorModal}
          onClose={this.handleCloseErrorModal}
          onButtonClick={this.handleTryAgainAfterError}
          theme={theme}
          message={t('qr_code_not_recognized')}
          buttonText={t('try_again')}
        />
      </Container>
    );
  }
}

export default withTranslation()(withTheme(ScannedAttendees));
